import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Button, Space, Tooltip } from 'antd'
import { InfoCircleFilled } from '@ant-design/icons'
import RadioTabs from '../../../components/RadioTabs'
import Search from '../../../components/SearchFilter'
import TableAntd from '../../../components/TableAntd'
import useHook, { orderassuranceTabKey } from './hooks'
import style from './style.module.scss'
import Buttonbar from '../../../components/ButtonBar'
import {
  AccountStatusKey,
  openInvoiceStateDictInvert,
  signVoucherStateDict,
} from '../../../tools/state'
import { handleJumpAddInvoice } from '../../order-invoice/hooks'
import moment from 'moment'
import { OrderPageRes } from '@/api/orderManage'
import OverdueFeedbackPopover from './components/OverdueFeedbackPopover'

export const orderDetailLinkRender = (text: any, record: any) => {
  return (
    <>
      <Link to={`/srm/orderdetail?id=${record.orderId || record.id}`}>
        {text}
      </Link>
    </>
  )
}

/** 发货/妥投时效截止时间文案 */
const formatAgingDeadline = (
  time: number | null | undefined,
  actionWord: '发货' | '妥投',
) => {
  if (time == null) return ''
  const m = moment(time)
  if (!m.isValid()) return ''
  return `${m.format('YYYY-MM-DD HH:mm')}前${actionWord}`
}

const renderShipmentAgingCell = (_: unknown, record: OrderPageRes) => {
  const line = formatAgingDeadline(record.shipmentOverdueTime, '发货')
  const content = !line ? (
    <span>-</span>
  ) : record.shipmentOverdue ? (
    // 该内容会渲染进 OverdueFeedbackPopover 的 button 触发器，用 span 保持合法嵌套
    <span style={{ color: '#ff0303', lineHeight: 1.5, display: 'block' }}>
      <span style={{ display: 'block' }}>{line}</span>
      <span style={{ display: 'block' }}>{record.shipmentOverdueDesc}</span>
    </span>
  ) : (
    <span>{line}</span>
  )
  return (
    <OverdueFeedbackPopover
      type="shipment"
      records={record.shipmentFeedbackList}
    >
      {content}
    </OverdueFeedbackPopover>
  )
}

const renderProperAgingCell = (_: unknown, record: OrderPageRes) => {
  const line = formatAgingDeadline(record.properOverdueTime, '妥投')
  const content = !line ? (
    <span>-</span>
  ) : record.properOverdue && record.properOverdueDesc ? (
    // 该内容会渲染进 OverdueFeedbackPopover 的 button 触发器，用 span 保持合法嵌套
    <span style={{ color: '#ff0303', lineHeight: 1.5, display: 'block' }}>
      <span style={{ display: 'block' }}>{line}</span>
      <span style={{ display: 'block' }}>{record.properOverdueDesc}</span>
    </span>
  ) : (
    <span>{line}</span>
  )
  return (
    <OverdueFeedbackPopover type="proper" records={record.properFeedbackList}>
      {content}
    </OverdueFeedbackPopover>
  )
}

export default function OrderAssurance() {
  const {
    getHasFilterBtn,
    state,
    handleCheckedChange,
    handlePaginationChange,
    handleSearchClick,
    handleTableRefresh,
    handleTableChangeCheck,
    handleCheckParamChange,
    exportFile,
    batchDownloadContractFile,
    jumpToPaymentDetails,
  } = useHook()
  const {
    checkedRadioKey,
    currentRadioDict,
    dataSource,
    isTableLoading,
    pagination,
    checkGorupParam,
    searchParam,
  } = state

  const initialSearchFilterFieldKey = useMemo(() => {
    if (checkedRadioKey !== '订单超期') return undefined
    const ot = searchParam?.overdueType
    if (ot == null || String(ot).trim() === '') return undefined
    return 'overdueType' as const
  }, [checkedRadioKey, searchParam?.overdueType])

  const syncSearchFormValues = useMemo(():
    | Record<string, unknown>
    | undefined => {
    if (checkedRadioKey !== '订单超期') return undefined
    const ot = searchParam?.overdueType
    if (ot == null || String(ot).trim() === '') return undefined
    return { overdueType: String(ot) }
  }, [checkedRadioKey, searchParam?.overdueType])

  const toAssureOperationRender = (text: any, record: any) => {
    return (
      <Link to={`/srm/orderdetail?id=${record.orderId || record.id}`}>
        马上履约
      </Link>
    )
  }
  const ausuringOperationRender = (text: any, record: any) => {
    return (
      <Link to={`/srm/orderdetail?id=${record.orderId || record.id}`}>
        继续发货
      </Link>
    )
  }
  const toCheckOperationRender = (text: any, record: any) => {
    return (
      <Link to={`/srm/orderdetail?id=${record.orderId || record.id}`}>
        去验收
      </Link>
    )
  }
  // 供应商开票状态
  const openInvoiceStateRender = (text: AccountStatusKey) => {
    const styleDict: any = {
      // 供应商已开票
      '3': {
        color: 'green',
      },
      // 供应商开票驳回
      '4': {
        color: 'red',
      },
    }
    return (
      <span style={styleDict[text]}>
        {openInvoiceStateDictInvert?.[text]?.name ?? '-'}
      </span>
    )
  }

  // 签收凭证
  const signVoucherStateDictRender = (text: any) => {
    const styleDict: any = {
      // 签收凭证已确认
      '3': {
        color: 'green',
      },
      // 签收凭证驳回
      '2': {
        color: 'red',
      },
    }
    return (
      <span style={styleDict[text]}>
        {(signVoucherStateDict as any)?.[text]}
      </span>
    )
  }

  // 客户回款
  const customerReturnProgressRender = (text: string) => {
    const styleDict: any = {
      // 客户回款已完成
      已完成: {
        color: 'green',
      },
    }
    return <span style={styleDict[text]}>{text}</span>
  }

  // 客户开票的跳转
  const invoicingStateRender = (text: any, record: any) => {
    const styleDict: any = {
      已开票: {
        color: 'green',
      },
      未开票: {
        color: 'red',
      },
    }
    return (
      <Space>
        <span style={styleDict[text]}>{text}</span>
        {record.invoicingState === '未开票' &&
        (record.orderState === '已完成' || record.orderState === '待验收') ? (
          <span
            className="primaryBtn"
            onClick={() => handleJumpAddInvoice([record.orderId || record.id])}
          >
            {'申请>>'}
          </span>
        ) : null}
      </Space>
    )
  }

  // 订单状态
  const withTagRender = (text: any, record: any) => {
    return (
      <div>
        <span>{text}</span>
        {record?.salesReturnState ? (
          <span className="return-tag">有退货</span>
        ) : null}
        {record.cancelState ? (
          <span className="newCancel-tag">有取消</span>
        ) : null}
      </div>
    )
  }

  // 派单时间
  const dispatchTimeRender = (text: number) =>
    moment(text).format('YYYY-MM-DD HH:mm:ss')

  const listColumnDict: { [x in orderassuranceTabKey]: any } = {
    待履约: {
      listColumn: [
        {
          key: 'operation',
          title: '操作',
          render: toAssureOperationRender,
        },
        {
          key: 'orderNo',
          title: '客户订单号',
          width: 200,
          render: withTagRender,
        },
        {
          key: 'createTime',
          title: '下单时间',
        },
        {
          key: 'dispatchTime',
          title: '派单时间',
          render: dispatchTimeRender,
        },
        {
          key: 'shipmentOverdueTime',
          title: '发货时效',
          width: 200,
          align: 'left',
          render: renderShipmentAgingCell,
        },
        {
          key: 'properOverdueTime',
          title: '妥投时效',
          width: 200,
          align: 'left',
          render: renderProperAgingCell,
        },
        {
          key: 'titleOfTheContract',
          title: '签约抬头',
          render: (text: string) => text || '万聚国际（杭州）供应链有限公司',
        },
        {
          key: 'platform',
          title: '下单平台',
        },
        {
          key: 'price',
          title: '下单金额',
        },
        {
          key: 'customer',
          title: '客户名称',
        },
        {
          key: 'consignee',
          title: '收件人',
        },
        {
          key: 'mobile',
          title: '联系方式',
        },
        {
          key: 'address',
          title: '收货地址',
        },
      ],
    },
    履约中: {
      listColumn: [
        {
          key: 'operation',
          title: '操作',
          render: ausuringOperationRender,
        },
        {
          key: 'progress',
          title: '发货进度',
        },
        {
          key: 'orderNo',
          title: '客户订单号',
          width: 200,
          render: withTagRender,
        },
        {
          key: 'createTime',
          title: '下单时间',
        },
        {
          key: 'dispatchTime',
          title: '派单时间',
          render: dispatchTimeRender,
        },
        {
          key: 'shipmentOverdueTime',
          title: '发货时效',
          width: 200,
          align: 'left',
          render: renderShipmentAgingCell,
        },
        {
          key: 'properOverdueTime',
          title: '妥投时效',
          width: 200,
          align: 'left',
          render: renderProperAgingCell,
        },
        {
          key: 'titleOfTheContract',
          title: '签约抬头',
          render: (text: string) => text || '万聚国际（杭州）供应链有限公司',
        },
        {
          key: 'platform',
          title: '下单平台',
        },
        {
          key: 'price',
          title: '下单金额',
        },
        {
          key: 'customer',
          title: '客户名称',
        },
        {
          key: 'consignee',
          title: '收件人',
        },
        {
          key: 'mobile',
          title: '联系方式',
        },
        {
          key: 'address',
          title: '收货地址',
        },
      ],
    },
    待验收: {
      listColumn: [
        {
          key: 'operation',
          title: '操作',
          render: toCheckOperationRender,
        },
        {
          key: 'orderNo',
          title: '客户订单号',
          width: 200,
          render: withTagRender,
        },
        {
          key: 'createTime',
          title: '下单时间',
        },
        {
          key: 'dispatchTime',
          title: '派单时间',
          render: dispatchTimeRender,
        },
        {
          key: 'shipmentOverdueTime',
          title: '发货时效',
          width: 200,
          align: 'left',
          render: renderShipmentAgingCell,
        },
        {
          key: 'properOverdueTime',
          title: '妥投时效',
          width: 200,
          align: 'left',
          render: renderProperAgingCell,
        },
        {
          key: 'titleOfTheContract',
          title: '签约抬头',
          render: (text: string) => text || '万聚国际（杭州）供应链有限公司',
        },
        {
          key: 'platform',
          title: '下单平台',
        },
        {
          key: 'price',
          title: '下单金额',
        },
        {
          key: 'customer',
          title: '客户名称',
        },
        {
          key: 'consignee',
          title: '收件人',
        },
        {
          key: 'mobile',
          title: '联系方式',
        },
        {
          key: 'address',
          title: '收货地址',
        },
      ],
    },
    订单超期: {
      listColumn: [
        {
          key: 'orderNo',
          title: '客户订单号',
          width: 200,
          render: orderDetailLinkRender,
        },
        {
          key: 'orderState',
          title: '订单状态',
          width: 180,
          render: withTagRender,
        },
        {
          key: 'platform',
          title: '下单平台',
        },
        {
          key: 'createTime',
          title: '下单时间',
        },
        {
          key: 'dispatchTime',
          title: '派单时间',
          render: dispatchTimeRender,
        },
        {
          key: 'shipmentOverdueTime',
          title: '发货时效',
          width: 200,
          align: 'left',
          render: renderShipmentAgingCell,
        },
        {
          key: 'properOverdueTime',
          title: '妥投时效',
          width: 200,
          align: 'left',
          render: renderProperAgingCell,
        },
        {
          key: 'titleOfTheContract',
          title: '签约抬头',
          render: (text: string) => text || '万聚国际（杭州）供应链有限公司',
        },
        {
          key: 'price',
          title: '下单金额',
        },
        {
          key: 'consignee',
          title: '收件人',
        },
        {
          key: 'customer',
          title: '客户名称',
        },
        {
          key: 'signVoucherState',
          title: '签收凭证',
          render: signVoucherStateDictRender,
        },
        {
          key: 'invoicingState',
          title: '客户开票',
          render: invoicingStateRender,
        },
        {
          key: 'customerReturnProgress',
          title: '对账完毕',
          render: customerReturnProgressRender,
        },
        {
          key: 'writeOffTime',
          title: '最新核销时间',
          render: (text: string) => text || '-',
        },
        {
          key: 'paymentType',
          title: '最新回款方式',
          render: (text: string) => text || '-',
        },
        {
          key: 'accountOpenInvoiceStatus',
          title: '供应商开票',
          render: openInvoiceStateRender,
        },
        {
          key: 'paymentStatus',
          title: '付款状态',
        },
      ],
    },
    已完成: {
      listColumn: [
        {
          key: 'orderNo',
          title: '客户订单号',
          width: 200,
          render: orderDetailLinkRender,
        },
        {
          key: 'orderState',
          title: '订单状态',
          width: 180,
          render: withTagRender,
        },
        {
          key: 'platform',
          title: '下单平台',
        },
        {
          key: 'createTime',
          title: '下单时间',
        },
        {
          key: 'dispatchTime',
          title: '派单时间',
          render: dispatchTimeRender,
        },
        {
          key: 'shipmentOverdueTime',
          title: '发货时效',
          width: 200,
          align: 'left',
          render: renderShipmentAgingCell,
        },
        {
          key: 'properOverdueTime',
          title: '妥投时效',
          width: 200,
          align: 'left',
          render: renderProperAgingCell,
        },
        {
          key: 'titleOfTheContract',
          title: '签约抬头',
          render: (text: string) => text || '万聚国际（杭州）供应链有限公司',
        },
        {
          key: 'price',
          title: '下单金额',
        },
        {
          key: 'customer',
          title: '客户名称',
        },
        {
          key: 'signVoucherState',
          title: '签收凭证',
          render: signVoucherStateDictRender,
        },
        {
          key: 'invoicingState',
          title: '客户开票',
          render: invoicingStateRender,
        },
        {
          key: 'customerReturnProgress',
          title: '对账完毕',
          render: customerReturnProgressRender,
        },
        {
          key: 'writeOffTime',
          title: '最新核销时间',
          render: (text: string) => text || '-',
        },
        {
          key: 'paymentType',
          title: '最新回款方式',
          render: (text: string) => text || '-',
        },
        {
          key: 'accountOpenInvoiceStatus',
          title: '供应商开票',
          render: openInvoiceStateRender,
        },
        {
          key: 'paymentStatus',
          title: '付款状态',
        },
      ],
    },
    全部: {
      listColumn: [
        {
          key: 'orderNo',
          title: '客户订单号',
          width: 200,
          render: orderDetailLinkRender,
        },
        {
          key: 'orderState',
          title: '订单状态',
          width: 180,
          render: withTagRender,
        },
        {
          key: 'platform',
          title: '下单平台',
        },
        {
          key: 'createTime',
          title: '下单时间',
        },
        {
          key: 'dispatchTime',
          title: '派单时间',
          render: dispatchTimeRender,
        },
        {
          key: 'shipmentOverdueTime',
          title: '发货时效',
          width: 200,
          align: 'left',
          render: renderShipmentAgingCell,
        },
        {
          key: 'properOverdueTime',
          title: '妥投时效',
          width: 200,
          align: 'left',
          render: renderProperAgingCell,
        },
        {
          key: 'titleOfTheContract',
          title: '签约抬头',
          render: (text: string) => text || '万聚国际（杭州）供应链有限公司',
        },
        {
          key: 'price',
          title: '下单金额',
        },
        {
          key: 'consignee',
          title: '收件人',
        },
        {
          key: 'customer',
          title: '客户名称',
        },
        {
          key: 'signVoucherState',
          title: '签收凭证',
          render: signVoucherStateDictRender,
        },
        {
          key: 'invoicingState',
          title: '客户开票',
          render: invoicingStateRender,
        },
        {
          key: 'customerReturnProgress',
          title: '对账完毕',
          render: customerReturnProgressRender,
        },
        {
          key: 'writeOffTime',
          title: '最新核销时间',
          render: (text: string) => text || '-',
        },
        {
          key: 'paymentType',
          title: '最新回款方式',
          render: (text: string) => text || '-',
        },
        {
          key: 'accountOpenInvoiceStatus',
          title: '供应商开票',
          render: openInvoiceStateRender,
        },
        {
          key: 'paymentStatus',
          title: '付款状态',
        },
      ],
    },
  }
  return (
    <div>
      <Search
        initialFilterFieldKey={initialSearchFilterFieldKey}
        syncFormValues={syncSearchFormValues}
        fieldsData={[
          'orderNo',
          'consignee',
          'customer',
          'customerPayback',
          'invoicingState',
          'accountOpenInvoiceStatus',
          'signVoucher',
          'paymentStatus',
          'address',
          'mobile',
          'startDate',
          'endDate',
          'orderState',
          'platform',
          'startWriteOffTime',
          'endWriteOffTime',
          'paymentTypeName',
          'materialCode',
          'productName',
          'brand',
          'orderRemark',
          'overdueType',
        ]}
        onSearch={handleSearchClick}
        keyName="ORDERASSURANCE"
        ScreenKey="ORDERASSURANCE"
      />
      <div className={style['inner-content']}>
        <div className={style['tab-container']}>
          <RadioTabs
            defaultKey={checkedRadioKey}
            radioItemDict={currentRadioDict}
            onChange={handleCheckedChange}
          ></RadioTabs>
          {checkedRadioKey === '待验收' ? (
            <Tooltip
              title={
                <div className={style['tooltip-card']}>
                  <div className={style['card-title']}>订单验收说明</div>
                  <div className={style['card-content']}>
                    订单已全部发货且所有验收单全部物流签收后，此时上传验收单即可完成验收
                  </div>
                </div>
              }
              placement="topLeft"
            >
              <div className={style['info-text']}>
                <InfoCircleFilled />
                <span className={style['info-content']}>订单验收说明</span>
              </div>
            </Tooltip>
          ) : null}
        </div>

        <div className={style['filter-btn-container']}>
          <Space size={'small'}>
            {getHasFilterBtn(checkedRadioKey) ? (
              <div
                className={
                  checkGorupParam?.salesReturnState
                    ? 'filter-btn-active'
                    : 'filter-btn'
                }
                onClick={() =>
                  handleCheckParamChange({
                    salesReturnState: !checkGorupParam?.salesReturnState,
                  })
                }
              >
                有退货
              </div>
            ) : null}
            {getHasFilterBtn(checkedRadioKey) ? (
              <div
                className={
                  checkGorupParam?.cancelState
                    ? 'filter-btn-active'
                    : 'filter-btn'
                }
                onClick={() =>
                  handleCheckParamChange({
                    cancelState: !checkGorupParam?.cancelState,
                  })
                }
              >
                有取消
              </div>
            ) : null}
          </Space>
        </div>
        <Buttonbar onRefresh={handleTableRefresh}>
          <Space>
            <Button onClick={batchDownloadContractFile}>
              批量下载采购合同
            </Button>
            {checkedRadioKey === '全部' && (
              <Button onClick={jumpToPaymentDetails}>客户回款明细</Button>
            )}
            {<Button onClick={exportFile}>导出</Button>}
          </Space>
        </Buttonbar>
        <TableAntd
          dataSource={dataSource}
          listColumn={listColumnDict[checkedRadioKey].listColumn}
          loading={isTableLoading}
          Y={380}
          pagination={{
            ...pagination,
            onChange: handlePaginationChange,
          }}
          showSize
          onChangeCheck={handleTableChangeCheck}
        ></TableAntd>
      </div>
    </div>
  )
}
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Button, Space, Tooltip } from 'antd'
import { InfoCircleFilled } from '@ant-design/icons'
import RadioTabs from '../../../components/RadioTabs'
import Search from '../../../components/SearchFilter'
import TableAntd from '../../../components/TableAntd'
import useHook, { orderassuranceTabKey } from './hooks'
import style from './style.module.scss'
import Buttonbar from '../../../components/ButtonBar'
import {
  AccountStatusKey,
  openInvoiceStateDictInvert,
  signVoucherStateDict,
} from '../../../tools/state'
import { handleJumpAddInvoice } from '../../order-invoice/hooks'
import moment from 'moment'
import { OrderPageRes } from '@/api/orderManage'
import OverdueFeedbackPopover from './components/OverdueFeedbackPopover'

export const orderDetailLinkRender = (text: any, record: any) => {
  return (
    <>
      <Link to={`/srm/orderdetail?id=${record.orderId || record.id}`}>
        {text}
      </Link>
    </>
  )
}

/** 发货/妥投时效截止时间文案 */
const formatAgingDeadline = (
  time: number | null | undefined,
  actionWord: '发货' | '妥投',
) => {
  if (time == null) return ''
  const m = moment(time)
  if (!m.isValid()) return ''
  return `${m.format('YYYY-MM-DD HH:mm')}前${actionWord}`
}

const renderShipmentAgingCell = (_: unknown, record: OrderPageRes) => {
  const line = formatAgingDeadline(record.shipmentOverdueTime, '发货')
  const content = !line ? (
    <span>-</span>
  ) : record.shipmentOverdue ? (
    // 该内容会渲染进 OverdueFeedbackPopover 的 button 触发器，用 span 保持合法嵌套
    <span style={{ color: '#ff0303', lineHeight: 1.5, display: 'block' }}>
      <span style={{ display: 'block' }}>{line}</span>
      <span style={{ display: 'block' }}>{record.shipmentOverdueDesc}</span>
    </span>
  ) : (
    <span>{line}</span>
  )
  return (
    <OverdueFeedbackPopover
      type="shipment"
      records={record.shipmentFeedbackList}
    >
      {content}
    </OverdueFeedbackPopover>
  )
}

const renderProperAgingCell = (_: unknown, record: OrderPageRes) => {
  const line = formatAgingDeadline(record.properOverdueTime, '妥投')
  const content = !line ? (
    <span>-</span>
  ) : record.properOverdue && record.properOverdueDesc ? (
    // 该内容会渲染进 OverdueFeedbackPopover 的 button 触发器，用 span 保持合法嵌套
    <span style={{ color: '#ff0303', lineHeight: 1.5, display: 'block' }}>
      <span style={{ display: 'block' }}>{line}</span>
      <span style={{ display: 'block' }}>{record.properOverdueDesc}</span>
    </span>
  ) : (
    <span>{line}</span>
  )
  return (
    <OverdueFeedbackPopover type="proper" records={record.properFeedbackList}>
      {content}
    </OverdueFeedbackPopover>
  )
}

export default function OrderAssurance() {
  const {
    getHasFilterBtn,
    state,
    handleCheckedChange,
    handlePaginationChange,
    handleSearchClick,
    handleTableRefresh,
    handleTableChangeCheck,
    handleCheckParamChange,
    exportFile,
    batchDownloadContractFile,
    jumpToPaymentDetails,
  } = useHook()
  const {
    checkedRadioKey,
    currentRadioDict,
    dataSource,
    isTableLoading,
    pagination,
    checkGorupParam,
    searchParam,
  } = state

  const initialSearchFilterFieldKey = useMemo(() => {
    if (checkedRadioKey !== '订单超期') return undefined
    const ot = searchParam?.overdueType
    if (ot == null || String(ot).trim() === '') return undefined
    return 'overdueType' as const
  }, [checkedRadioKey, searchParam?.overdueType])

  const syncSearchFormValues = useMemo(():
    | Record<string, unknown>
    | undefined => {
    if (checkedRadioKey !== '订单超期') return undefined
    const ot = searchParam?.overdueType
    if (ot == null || String(ot).trim() === '') return undefined
    return { overdueType: String(ot) }
  }, [checkedRadioKey, searchParam?.overdueType])

  const toAssureOperationRender = (text: any, record: any) => {
    return (
      <Link to={`/srm/orderdetail?id=${record.orderId || record.id}`}>
        马上履约
      </Link>
    )
  }
  const ausuringOperationRender = (text: any, record: any) => {
    return (
      <Link to={`/srm/orderdetail?id=${record.orderId || record.id}`}>
        继续发货
      </Link>
    )
  }
  const toCheckOperationRender = (text: any, record: any) => {
    return (
      <Link to={`/srm/orderdetail?id=${record.orderId || record.id}`}>
        去验收
      </Link>
    )
  }
  // 供应商开票状态
  const openInvoiceStateRender = (text: AccountStatusKey) => {
    const styleDict: any = {
      // 供应商已开票
      '3': {
        color: 'green',
      },
      // 供应商开票驳回
      '4': {
        color: 'red',
      },
    }
    return (
      <span style={styleDict[text]}>
        {openInvoiceStateDictInvert?.[text]?.name ?? '-'}
      </span>
    )
  }

  // 签收凭证
  const signVoucherStateDictRender = (text: any) => {
    const styleDict: any = {
      // 签收凭证已确认
      '3': {
        color: 'green',
      },
      // 签收凭证驳回
      '2': {
        color: 'red',
      },
    }
    return (
      <span style={styleDict[text]}>
        {(signVoucherStateDict as any)?.[text]}
      </span>
    )
  }

  // 客户回款
  const customerReturnProgressRender = (text: string) => {
    const styleDict: any = {
      // 客户回款已完成
      已完成: {
        color: 'green',
      },
    }
    return <span style={styleDict[text]}>{text}</span>
  }

  // 客户开票的跳转
  const invoicingStateRender = (text: any, record: any) => {
    const styleDict: any = {
      已开票: {
        color: 'green',
      },
      未开票: {
        color: 'red',
      },
    }
    return (
      <Space>
        <span style={styleDict[text]}>{text}</span>
        {record.invoicingState === '未开票' &&
        (record.orderState === '已完成' || record.orderState === '待验收') ? (
          <span
            className="primaryBtn"
            onClick={() => handleJumpAddInvoice([record.orderId || record.id])}
          >
            {'申请>>'}
          </span>
        ) : null}
      </Space>
    )
  }

  // 订单状态
  const withTagRender = (text: any, record: any) => {
    return (
      <div>
        <span>{text}</span>
        {record?.salesReturnState ? (
          <span className="return-tag">有退货</span>
        ) : null}
        {record.cancelState ? (
          <span className="newCancel-tag">有取消</span>
        ) : null}
      </div>
    )
  }

  // 派单时间
  const dispatchTimeRender = (text: number) =>
    moment(text).format('YYYY-MM-DD HH:mm:ss')

  const listColumnDict: { [x in orderassuranceTabKey]: any } = {
    待履约: {
      listColumn: [
        {
          key: 'operation',
          title: '操作',
          render: toAssureOperationRender,
        },
        {
          key: 'orderNo',
          title: '客户订单号',
          width: 200,
          render: withTagRender,
        },
        {
          key: 'createTime',
          title: '下单时间',
        },
        {
          key: 'dispatchTime',
          title: '派单时间',
          render: dispatchTimeRender,
        },
        {
          key: 'shipmentOverdueTime',
          title: '发货时效',
          width: 200,
          align: 'left',
          render: renderShipmentAgingCell,
        },
        {
          key: 'properOverdueTime',
          title: '妥投时效',
          width: 200,
          align: 'left',
          render: renderProperAgingCell,
        },
        {
          key: 'titleOfTheContract',
          title: '签约抬头',
          render: (text: string) => text || '万聚国际（杭州）供应链有限公司',
        },
        {
          key: 'platform',
          title: '下单平台',
        },
        {
          key: 'price',
          title: '下单金额',
        },
        {
          key: 'customer',
          title: '客户名称',
        },
        {
          key: 'consignee',
          title: '收件人',
        },
        {
          key: 'mobile',
          title: '联系方式',
        },
        {
          key: 'address',
          title: '收货地址',
        },
      ],
    },
    履约中: {
      listColumn: [
        {
          key: 'operation',
          title: '操作',
          render: ausuringOperationRender,
        },
        {
          key: 'progress',
          title: '发货进度',
        },
        {
          key: 'orderNo',
          title: '客户订单号',
          width: 200,
          render: withTagRender,
        },
        {
          key: 'createTime',
          title: '下单时间',
        },
        {
          key: 'dispatchTime',
          title: '派单时间',
          render: dispatchTimeRender,
        },
        {
          key: 'shipmentOverdueTime',
          title: '发货时效',
          width: 200,
          align: 'left',
          render: renderShipmentAgingCell,
        },
        {
          key: 'properOverdueTime',
          title: '妥投时效',
          width: 200,
          align: 'left',
          render: renderProperAgingCell,
        },
        {
          key: 'titleOfTheContract',
          title: '签约抬头',
          render: (text: string) => text || '万聚国际（杭州）供应链有限公司',
        },
        {
          key: 'platform',
          title: '下单平台',
        },
        {
          key: 'price',
          title: '下单金额',
        },
        {
          key: 'customer',
          title: '客户名称',
        },
        {
          key: 'consignee',
          title: '收件人',
        },
        {
          key: 'mobile',
          title: '联系方式',
        },
        {
          key: 'address',
          title: '收货地址',
        },
      ],
    },
    待验收: {
      listColumn: [
        {
          key: 'operation',
          title: '操作',
          render: toCheckOperationRender,
        },
        {
          key: 'orderNo',
          title: '客户订单号',
          width: 200,
          render: withTagRender,
        },
        {
          key: 'createTime',
          title: '下单时间',
        },
        {
          key: 'dispatchTime',
          title: '派单时间',
          render: dispatchTimeRender,
        },
        {
          key: 'shipmentOverdueTime',
          title: '发货时效',
          width: 200,
          align: 'left',
          render: renderShipmentAgingCell,
        },
        {
          key: 'properOverdueTime',
          title: '妥投时效',
          width: 200,
          align: 'left',
          render: renderProperAgingCell,
        },
        {
          key: 'titleOfTheContract',
          title: '签约抬头',
          render: (text: string) => text || '万聚国际（杭州）供应链有限公司',
        },
        {
          key: 'platform',
          title: '下单平台',
        },
        {
          key: 'price',
          title: '下单金额',
        },
        {
          key: 'customer',
          title: '客户名称',
        },
        {
          key: 'consignee',
          title: '收件人',
        },
        {
          key: 'mobile',
          title: '联系方式',
        },
        {
          key: 'address',
          title: '收货地址',
        },
      ],
    },
    订单超期: {
      listColumn: [
        {
          key: 'orderNo',
          title: '客户订单号',
          width: 200,
          render: orderDetailLinkRender,
        },
        {
          key: 'orderState',
          title: '订单状态',
          width: 180,
          render: withTagRender,
        },
        {
          key: 'platform',
          title: '下单平台',
        },
        {
          key: 'createTime',
          title: '下单时间',
        },
        {
          key: 'dispatchTime',
          title: '派单时间',
          render: dispatchTimeRender,
        },
        {
          key: 'shipmentOverdueTime',
          title: '发货时效',
          width: 200,
          align: 'left',
          render: renderShipmentAgingCell,
        },
        {
          key: 'properOverdueTime',
          title: '妥投时效',
          width: 200,
          align: 'left',
          render: renderProperAgingCell,
        },
        {
          key: 'titleOfTheContract',
          title: '签约抬头',
          render: (text: string) => text || '万聚国际（杭州）供应链有限公司',
        },
        {
          key: 'price',
          title: '下单金额',
        },
        {
          key: 'consignee',
          title: '收件人',
        },
        {
          key: 'customer',
          title: '客户名称',
        },
        {
          key: 'signVoucherState',
          title: '签收凭证',
          render: signVoucherStateDictRender,
        },
        {
          key: 'invoicingState',
          title: '客户开票',
          render: invoicingStateRender,
        },
        {
          key: 'customerReturnProgress',
          title: '对账完毕',
          render: customerReturnProgressRender,
        },
        {
          key: 'writeOffTime',
          title: '最新核销时间',
          render: (text: string) => text || '-',
        },
        {
          key: 'paymentType',
          title: '最新回款方式',
          render: (text: string) => text || '-',
        },
        {
          key: 'accountOpenInvoiceStatus',
          title: '供应商开票',
          render: openInvoiceStateRender,
        },
        {
          key: 'paymentStatus',
          title: '付款状态',
        },
      ],
    },
    已完成: {
      listColumn: [
        {
          key: 'orderNo',
          title: '客户订单号',
          width: 200,
          render: orderDetailLinkRender,
        },
        {
          key: 'orderState',
          title: '订单状态',
          width: 180,
          render: withTagRender,
        },
        {
          key: 'platform',
          title: '下单平台',
        },
        {
          key: 'createTime',
          title: '下单时间',
        },
        {
          key: 'dispatchTime',
          title: '派单时间',
          render: dispatchTimeRender,
        },
        {
          key: 'shipmentOverdueTime',
          title: '发货时效',
          width: 200,
          align: 'left',
          render: renderShipmentAgingCell,
        },
        {
          key: 'properOverdueTime',
          title: '妥投时效',
          width: 200,
          align: 'left',
          render: renderProperAgingCell,
        },
        {
          key: 'titleOfTheContract',
          title: '签约抬头',
          render: (text: string) => text || '万聚国际（杭州）供应链有限公司',
        },
        {
          key: 'price',
          title: '下单金额',
        },
        {
          key: 'customer',
          title: '客户名称',
        },
        {
          key: 'signVoucherState',
          title: '签收凭证',
          render: signVoucherStateDictRender,
        },
        {
          key: 'invoicingState',
          title: '客户开票',
          render: invoicingStateRender,
        },
        {
          key: 'customerReturnProgress',
          title: '对账完毕',
          render: customerReturnProgressRender,
        },
        {
          key: 'writeOffTime',
          title: '最新核销时间',
          render: (text: string) => text || '-',
        },
        {
          key: 'paymentType',
          title: '最新回款方式',
          render: (text: string) => text || '-',
        },
        {
          key: 'accountOpenInvoiceStatus',
          title: '供应商开票',
          render: openInvoiceStateRender,
        },
        {
          key: 'paymentStatus',
          title: '付款状态',
        },
      ],
    },
    全部: {
      listColumn: [
        {
          key: 'orderNo',
          title: '客户订单号',
          width: 200,
          render: orderDetailLinkRender,
        },
        {
          key: 'orderState',
          title: '订单状态',
          width: 180,
          render: withTagRender,
        },
        {
          key: 'platform',
          title: '下单平台',
        },
        {
          key: 'createTime',
          title: '下单时间',
        },
        {
          key: 'dispatchTime',
          title: '派单时间',
          render: dispatchTimeRender,
        },
        {
          key: 'shipmentOverdueTime',
          title: '发货时效',
          width: 200,
          align: 'left',
          render: renderShipmentAgingCell,
        },
        {
          key: 'properOverdueTime',
          title: '妥投时效',
          width: 200,
          align: 'left',
          render: renderProperAgingCell,
        },
        {
          key: 'titleOfTheContract',
          title: '签约抬头',
          render: (text: string) => text || '万聚国际（杭州）供应链有限公司',
        },
        {
          key: 'price',
          title: '下单金额',
        },
        {
          key: 'consignee',
          title: '收件人',
        },
        {
          key: 'customer',
          title: '客户名称',
        },
        {
          key: 'signVoucherState',
          title: '签收凭证',
          render: signVoucherStateDictRender,
        },
        {
          key: 'invoicingState',
          title: '客户开票',
          render: invoicingStateRender,
        },
        {
          key: 'customerReturnProgress',
          title: '对账完毕',
          render: customerReturnProgressRender,
        },
        {
          key: 'writeOffTime',
          title: '最新核销时间',
          render: (text: string) => text || '-',
        },
        {
          key: 'paymentType',
          title: '最新回款方式',
          render: (text: string) => text || '-',
        },
        {
          key: 'accountOpenInvoiceStatus',
          title: '供应商开票',
          render: openInvoiceStateRender,
        },
        {
          key: 'paymentStatus',
          title: '付款状态',
        },
      ],
    },
  }
  return (
    <div>
      <Search
        initialFilterFieldKey={initialSearchFilterFieldKey}
        syncFormValues={syncSearchFormValues}
        fieldsData={[
          'orderNo',
          'consignee',
          'customer',
          'customerPayback',
          'invoicingState',
          'accountOpenInvoiceStatus',
          'signVoucher',
          'paymentStatus',
          'address',
          'mobile',
          'startDate',
          'endDate',
          'orderState',
          'platform',
          'startWriteOffTime',
          'endWriteOffTime',
          'paymentTypeName',
          'materialCode',
          'productName',
          'brand',
          'orderRemark',
          'overdueType',
        ]}
        onSearch={handleSearchClick}
        keyName="ORDERASSURANCE"
        ScreenKey="ORDERASSURANCE"
      />
      <div className={style['inner-content']}>
        <div className={style['tab-container']}>
          <RadioTabs
            defaultKey={checkedRadioKey}
            radioItemDict={currentRadioDict}
            onChange={handleCheckedChange}
          ></RadioTabs>
          {checkedRadioKey === '待验收' ? (
            <Tooltip
              title={
                <div className={style['tooltip-card']}>
                  <div className={style['card-title']}>订单验收说明</div>
                  <div className={style['card-content']}>
                    订单已全部发货且所有验收单全部物流签收后，此时上传验收单即可完成验收
                  </div>
                </div>
              }
              placement="topLeft"
            >
              <div className={style['info-text']}>
                <InfoCircleFilled />
                <span className={style['info-content']}>订单验收说明</span>
              </div>
            </Tooltip>
          ) : null}
        </div>

        <div className={style['filter-btn-container']}>
          <Space size={'small'}>
            {getHasFilterBtn(checkedRadioKey) ? (
              <div
                className={
                  checkGorupParam?.salesReturnState
                    ? 'filter-btn-active'
                    : 'filter-btn'
                }
                onClick={() =>
                  handleCheckParamChange({
                    salesReturnState: !checkGorupParam?.salesReturnState,
                  })
                }
              >
                有退货
              </div>
            ) : null}
            {getHasFilterBtn(checkedRadioKey) ? (
              <div
                className={
                  checkGorupParam?.cancelState
                    ? 'filter-btn-active'
                    : 'filter-btn'
                }
                onClick={() =>
                  handleCheckParamChange({
                    cancelState: !checkGorupParam?.cancelState,
                  })
                }
              >
                有取消
              </div>
            ) : null}
          </Space>
        </div>
        <Buttonbar onRefresh={handleTableRefresh}>
          <Space>
            <Button onClick={batchDownloadContractFile}>
              批量下载采购合同
            </Button>
            {checkedRadioKey === '全部' && (
              <Button onClick={jumpToPaymentDetails}>客户回款明细</Button>
            )}
            {<Button onClick={exportFile}>导出</Button>}
          </Space>
        </Buttonbar>
        <TableAntd
          dataSource={dataSource}
          listColumn={listColumnDict[checkedRadioKey].listColumn}
          loading={isTableLoading}
          Y={380}
          pagination={{
            ...pagination,
            onChange: handlePaginationChange,
          }}
          showSize
          onChangeCheck={handleTableChangeCheck}
        ></TableAntd>
      </div>
    </div>
  )
}
